import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { doc, getDoc, collection, addDoc, getDocs, updateDoc } from "firebase/firestore";
import { Button, Row, Col, Card, Form, Container, ProgressBar, Alert, Badge, FormControl } from "react-bootstrap";
import Header from "../layouts/Header";

export default function SignedDocuments() {
  const [documents, setDocuments] = useState([]);
  const [unsignedFile, setUnsignedFile] = useState(null);
  const [signedFile, setSignedFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [createdForEmail, setCreatedForEmail] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [canSendSignRequests, setCanSendSignRequests] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [createdForFilter, setCreatedForFilter] = useState("");

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        setEmail(user.email);
        const userDocRef = doc(db, "Users", user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userRole = userDoc.data().role || "user";
          setRole(userRole);
          await fetchRolePermissions(userRole); // Fetch permissions based on role
        }
      }
    };

    fetchUserRole();
    fetchDocuments();
  }, []);

  const fetchRolePermissions = async (userRole) => {
    const rolesDocRef = collection(db, "Roles");
    const rolesSnapshot = await getDocs(rolesDocRef);
    const roles = rolesSnapshot.docs.map((doc) => doc.data());

    const matchedRole = roles.find((role) => role.name === userRole);
    if (matchedRole && matchedRole.permissions) {
      setCanSendSignRequests(matchedRole.permissions.canSendSignRequests || false);
    }
  };

  const fetchDocuments = async () => {
    const documentSnapshot = await getDocs(collection(db, "SignedDocuments"));
    const allDocuments = documentSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const userEmail = auth.currentUser.email;
    const filteredDocuments =
      role === "admin"
        ? allDocuments
        : allDocuments.filter(
            (doc) => doc.createdBy.email === userEmail || doc.createdFor === userEmail
          );

    setDocuments(filteredDocuments);
  };

  const handleFileUpload = (e, setFile) => {
    setFile(e.target.files[0]);
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    const originalName = file.name.replace(/\.\w+$/, ""); // Remove any existing extensions
    formData.append("file", file);
    formData.append("public_id", originalName); // Use clean name
    formData.append("upload_preset", "documents");

    const response = await fetch("https://api.cloudinary.com/v1_1/dwcbtliac/upload", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to upload file to Cloudinary");
    }

    const data = await response.json();
    console.log("Cloudinary Response:", data);

    // Ensure URL consistency
    const cleanUrl = data.secure_url.replace(/\.docx\.docx$/, ".docx");
    console.log("Clean URL:", cleanUrl);

    return cleanUrl; // Return the processed URL
  };

  const handleUpload = async () => {
    if (!unsignedFile || !createdForEmail || !name || !description) {
      alert("Please fill in all required fields (file, recipient email, name, description).");
      return;
    }

    setUploading(true);

    try {
      const unsignedUrl = await uploadToCloudinary(unsignedFile);

      const documentRef = collection(db, "SignedDocuments");
      await addDoc(documentRef, {
        name,
        description,
        unsignedUrl,
        signedUrl: null,
        signedStatus: "Pending",
        createdAt: new Date(),
        createdBy: { email },
        createdFor: createdForEmail,
      });

      setUnsignedFile(null);
      setName("");
      setDescription("");
      setCreatedForEmail("");
      setUploading(false);
      fetchDocuments();
    } catch (error) {
      console.error("File upload failed:", error);
      setUploading(false);
    }
  };

  const handleSignedUpload = async (docId) => {
    if (!signedFile) {
      alert("Please select a signed document.");
      return;
    }

    setUploading(true);

    try {
      const signedUrl = await uploadToCloudinary(signedFile);

      const documentRef = doc(db, "SignedDocuments", docId);
      await updateDoc(documentRef, {
        signedUrl,
        signedStatus: "Completed",
      });

      setSignedFile(null);
      setUploading(false);
      fetchDocuments();
    } catch (error) {
      console.error("File upload failed:", error);
      setUploading(false);
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const documentName = doc.name || ""; // Default to empty string if name is undefined
    const createdByEmail = doc.createdBy.email || "";
    const createdForEmail = doc.createdFor || "";

    return (
      documentName.toLowerCase().includes(filterQuery) &&
      createdByEmail.toLowerCase().includes(createdByFilter) &&
      createdForEmail.toLowerCase().includes(createdForFilter)
    );
  });

  const pendingDocuments = filteredDocuments.filter((doc) => doc.signedStatus !== "Completed");
  const completedDocuments = filteredDocuments.filter((doc) => doc.signedStatus === "Completed");

  return (
    <React.Fragment>
      <Header />
      <Container className="main main-app p-4 items-center">
        <Alert variant="info">
          {role === "admin"
            ? "You are logged in as an admin and can view all signed document requests."
            : "You can view and manage document requests created by you or assigned to you."}
        </Alert>

        {(role === "admin" || canSendSignRequests) && (
          <>
            <Alert variant="info">You have access to send signature requests.</Alert>
            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <h4 className="text-primary">Request a Signature</h4>
                <Form.Group controlId="name" className="mt-3">
                  <Form.Label>Document Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter document name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Form.Group>
                <Form.Group controlId="description" className="mt-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter document description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Form.Group>
                <Form.Group controlId="unsignedFile" className="mt-3">
                  <Form.Label>Unsigned Document</Form.Label>
                  <Form.Control type="file" onChange={(e) => handleFileUpload(e, setUnsignedFile)} />
                </Form.Group>
                <Form.Group controlId="createdForEmail" className="mt-3">
                  <Form.Label>Recipient Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter recipient's email"
                    value={createdForEmail}
                    onChange={(e) => setCreatedForEmail(e.target.value)}
                  />
                </Form.Group>
                <Button className="mt-3" onClick={handleUpload} disabled={uploading}>
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
                {uploading && <ProgressBar now={uploadProgress} className="mt-3" />}
              </Card.Body>
            </Card>
          </>
        )}

        <h2 className="text-primary mb-4">Filter Documents</h2>

        <FormControl
          type="text"
          placeholder="Filter documents by name"
          className="mb-3"
          onChange={(e) => setFilterQuery(e.target.value.toLowerCase())}
        />
        <FormControl
          type="text"
          placeholder="Filter by Created By"
          className="mb-3"
          onChange={(e) => setCreatedByFilter(e.target.value.toLowerCase())}
        />
        <FormControl
          type="text"
          placeholder="Filter by Created For"
          className="mb-3"
          onChange={(e) => setCreatedForFilter(e.target.value.toLowerCase())}
        />

        <h4 className="text-warning mb-4">Pending Documents</h4>
        <Row>
          {pendingDocuments.map((doc) => (
            <Col md={6} key={doc.id}>
              <Card className="mb-4 shadow-sm">
                <Card.Body>
                  <h5>{doc.name}</h5>
                  <p className="text-muted">{doc.description}</p>
                  <p>
                    <strong>Created By:</strong> {doc.createdBy.email}
                  </p>
                  <p>
                    <strong>Created For:</strong> {doc.createdFor}
                  </p>
                  <p>
                    <strong>Created At:</strong>{" "}
                    {new Date(doc.createdAt.seconds * 1000).toLocaleString()}
                  </p>
                  <Badge bg="warning">Status: {doc.signedStatus}</Badge>
                  <Row className="mt-3">
                    <Col>
                      <h6>Unsigned Document</h6>
                      <a
                        href={doc.unsignedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                      >
                        View Unsigned Document
                      </a>
                    </Col>
                    <Col>
                      <h6>Upload Signed Document</h6>
                      <Form.Group>
                        <Form.Control
                          type="file"
                          onChange={(e) => handleFileUpload(e, setSignedFile)}
                        />
                      </Form.Group>
                      <Button
                        className="mt-2"
                        onClick={() => handleSignedUpload(doc.id)}
                        disabled={uploading}
                      >
                        {uploading ? "Uploading..." : "Upload"}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <h4 className="text-success mb-4">Completed Documents</h4>
        <Row>
          {completedDocuments.map((doc) => (
            <Col md={6} key={doc.id}>
              <Card className="mb-4 shadow-sm">
                <Card.Body>
                  <h5>{doc.name}</h5>
                  <p className="text-muted">{doc.description}</p>
                  <p>
                    <strong>Created By:</strong> {doc.createdBy.email}
                  </p>
                  <p>
                    <strong>Created For:</strong> {doc.createdFor}
                  </p>
                  <p>
                    <strong>Created At:</strong>{" "}
                    {new Date(doc.createdAt.seconds * 1000).toLocaleString()}
                  </p>
                  <Badge bg="success">Status: {doc.signedStatus}</Badge>
                  <Row className="mt-3">
                    <Col>
                      <h6>Unsigned Document</h6>
                      <a
                        href={doc.unsignedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                      >
                        View Unsigned Document
                      </a>
                    </Col>
                    <Col>
                      <h6>Signed Document</h6>
                      <a
                        href={doc.signedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-success btn-sm"
                      >
                        View Signed Document
                      </a>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </React.Fragment>
  );
}
